import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, User, ClipboardList, FileText, Award, Loader2, AlertCircle } from "lucide-react";
import { useAdminLogbookDetailQuery } from "../api/adminLogbookApi";
import { LbStatusBadge, fmtDate, memberName } from "../components/adminLogbookUi";

const DAYS = ["", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-800">{value ?? <span className="text-gray-400 italic">—</span>}</span>
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5">
        {icon}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function AdminLogbookDetailPage() {
  const { id = "" } = useParams();
  const { data: lb, isLoading, isError } = useAdminLogbookDetailQuery(id, { skip: !id });

  const entries = useMemo(() => {
    const list: any[] = Array.isArray(lb?.entries) ? lb.entries : [];
    return [...list].sort((a, b) => (a.week - b.week) || (a.day - b.day));
  }, [lb]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-24 flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
        <p className="text-sm">Loading logbook…</p>
      </div>
    );
  }

  if (isError || !lb) {
    return (
      <div className="max-w-4xl mx-auto p-6 py-20 text-center space-y-3">
        <AlertCircle className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-sm text-red-500">Could not load this logbook.</p>
        <Link to="/admin/logbooks" className="inline-block px-4 py-2 rounded-lg border text-sm hover:bg-gray-50">Back to diets</Link>
      </div>
    );
  }

  const reportUrl = lb.report_url || lb.report?.file_url || null;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-5">
      <Link to={lb.logbook_diet_id ? `/admin/logbooks/diet/${lb.logbook_diet_id}` : "/admin/logbooks"} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800">
        <ArrowLeft className="w-4 h-4" /> Back to logbooks
      </Link>

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-indigo-600 to-blue-700 text-white p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 text-indigo-100 text-xs font-semibold mb-1.5 bg-white/10 px-2.5 py-1 rounded-full">
              <BookOpen className="w-3.5 h-3.5" /> Logbook · Stage {lb.stage ?? "—"}
            </div>
            <h1 className="text-2xl font-bold">{lb.user?.name ?? "—"}</h1>
            <p className="text-sm text-indigo-100 mt-1">
              {lb.diet?.title ?? "—"} · started {fmtDate(lb.created_at)}
            </p>
          </div>
          <LbStatusBadge status={lb.status} />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Member */}
          <Section icon={<User className="w-4 h-4" />} title="Member">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" value={lb.user?.name} />
              <Field label="Membership No." value={lb.user?.member?.membership_no} />
              <Field label="Email" value={lb.user?.email} />
              <Field label="Phone" value={lb.user?.phone_number} />
            </div>
          </Section>

          {/* Entries */}
          <Section icon={<ClipboardList className="w-4 h-4" />} title={`Weekly Entries (${entries.length})`}>
            {entries.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No entries recorded.</p>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase border-b bg-gray-50">
                      <th className="px-3 py-2 font-semibold">Wk</th>
                      <th className="px-3 py-2 font-semibold">Day</th>
                      <th className="px-3 py-2 font-semibold">Subject</th>
                      <th className="px-3 py-2 font-semibold">Activity</th>
                      <th className="px-3 py-2 font-semibold">Date</th>
                      <th className="px-3 py-2 font-semibold">Hrs</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {entries.map((e) => (
                      <tr key={e.id} className="align-top">
                        <td className="px-3 py-2 text-gray-700">{e.week}</td>
                        <td className="px-3 py-2 text-gray-700">{DAYS[e.day] ?? e.day}</td>
                        <td className="px-3 py-2 text-gray-700">{e.subject ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-700 max-w-xs">{e.activity ?? "—"}</td>
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{fmtDate(e.entry_date)}</td>
                        <td className="px-3 py-2 text-gray-700">{e.hours ?? "—"}</td>
                        <td className="px-3 py-2"><LbStatusBadge status={e.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <Section icon={<BookOpen className="w-4 h-4" />} title="Logbook">
            <div className="grid grid-cols-1 gap-4">
              <Field label="Stage" value={`Stage ${lb.stage ?? "—"}`} />
              <Field label="Status" value={<LbStatusBadge status={lb.status} />} />
              <Field label="Progress" value={lb.position != null && lb.size != null ? `${lb.position} / ${lb.size} weeks` : "—"} />
              <Field label="Outcome" value={lb.final_grade ?? lb.testing_grade ?? "—"} />
              <Field label="Total score" value={lb.total_score ?? "—"} />
              <Field label="Submitted" value={lb.submitted_at ? fmtDate(lb.submitted_at) : "Not submitted"} />
            </div>
          </Section>

          <Section icon={<User className="w-4 h-4" />} title="Supervision & Assessment">
            <div className="grid grid-cols-1 gap-4">
              <Field label="Supervisor" value={memberName(lb.supervisor)} />
              <Field label="Assessor" value={memberName(lb.assessor)} />
              <Field label="Assessment fee" value={lb.assessment_paid ? `Paid ${lb.assessment_paid_at ? "· " + fmtDate(lb.assessment_paid_at) : ""}` : "Unpaid"} />
            </div>
          </Section>

          <Section icon={<FileText className="w-4 h-4" />} title="Report">
            {reportUrl ? (
              <a href={reportUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:underline">
                <FileText className="w-4 h-4" /> View report{lb.report_pages ? ` (${lb.report_pages} pages)` : ""}
              </a>
            ) : (
              <p className="text-sm text-gray-400 italic">No report uploaded.</p>
            )}
          </Section>

          {lb.remarks && (
            <Section icon={<Award className="w-4 h-4" />} title="Remarks">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{lb.remarks}</p>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
